import { getList } from '@/requests/api/get-list';
import {
  RealEstateConfig,
  type ProtectedIdentity,
  type RealEstate,
} from '@pigello/pigello-matrix';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';
import { getPerms } from '@/lib/perms';

export async function handleProtectedIdentityNestedCase(
  listResponse: TCustomList<ProtectedIdentity>
): Promise<TCustomList<ProtectedIdentity>> {
  const { canView } = getPerms('protectedidentity');
  if (!canView) return listResponse;

  const sourceObjectIds = listResponse.data.reduce<Set<string>>(
    (acc, protectedIdentity) => {
      if (protectedIdentity.tenant?.realestates?.at(0)?.id) {
        acc.add(protectedIdentity.tenant.realestates[0].id);
      }
      return acc;
    },
    new Set()
  );

  if (sourceObjectIds.size === 0) return listResponse;

  const chunkedSourceIds = chunkIds(sourceObjectIds);
  const resolvedSourcesPromises = await Promise.allSettled(
    chunkedSourceIds.map((chunk) =>
      getList<RealEstate>({
        modelName: RealEstateConfig.modelName,
        queryParams: {
          page: 1,
          pageSize: chunk.length,
          filters: {
            id: {
              __in: chunk.join(','),
            },
          },
        },
      })
    )
  );

  const resolvedSourceObjects: RealEstate[] = [];
  const failedSourceObjects: unknown[] = [];
  for (const promise of resolvedSourcesPromises) {
    if (promise.status === 'fulfilled') {
      resolvedSourceObjects.push(...(promise.value.list ?? []));
    } else {
      failedSourceObjects.push(promise.reason);
    }
  }

  return {
    ...listResponse,
    data: listResponse.data.map((protectedIdentity) => {
      const sourceRealEstateId =
        protectedIdentity?.tenant?.realestates?.at(0)?.id;
      if (!sourceRealEstateId) {
        return protectedIdentity;
      }

      const sourceRealEstate = resolvedSourceObjects.find(
        (realEstate) => realEstate.id === sourceRealEstateId
      );
      if (!sourceRealEstate) return protectedIdentity;

      return {
        ...protectedIdentity,
        tenant: {
          ...protectedIdentity.tenant,
          realestates: [
            sourceRealEstate,
            ...(protectedIdentity.tenant?.realestates?.filter(
              (realEstate) => realEstate.id !== sourceRealEstateId
            ) ?? []),
          ],
        },
      } as ProtectedIdentity;
    }),
  };
}
